import { Box, IconButton, Typography } from '@mui/material'; // Avatar,
import { CustomDivider } from '../custom-divider/CustomDivider';
import { CustomTooltip, TooltipProp } from '../custom-tooltip/CustomTooltip';
import InfoDrawerTrigger from '../info-drawer-trigger/InfoDrawerTrigger';
import variableStyle from '../../scss/_variables.scss';
import { resolveModuleHeaderInfoByTitle } from 'src/constants/moduleHelp';

export interface ModuleInfoDrawerProps {
  title: string;
  markdownContent: string;
  ariaLabel?: string;
  iconButtonId?: string;
}

export interface ModuleHeaderProps {
  title: string;
  visualStyle?: 'legacy' | 'revamp';
  ModuleHeaderIcon?: React.ReactNode;
  onModuleHeaderIconClick?: () => void;
  toolTipProps?: TooltipProp;
  infoDrawerProps?: ModuleInfoDrawerProps;
  rightContent?: React.ReactNode;
}

const appendQuickLinksToDrawer = (
  markdownContent: string,
  toolTipProps?: TooltipProp,
): string => {
  const contentSections = [markdownContent.trim()];

  const quickLinks: string[] = [];

  if (toolTipProps?.docResources) {
    quickLinks.push(`- [Documentation](${toolTipProps.docResources})`);
  }

  if (toolTipProps?.youTubeResources) {
    quickLinks.push(`- [Video guide](${toolTipProps.youTubeResources})`);
  }

  if (quickLinks.length > 0) {
    contentSections.push(`## Quick links\n\n${quickLinks.join('\n')}`);
  }

  return contentSections.filter(Boolean).join('\n\n');
};

const buildFallbackDrawerPropsFromTooltip = (
  title: string,
  toolTipProps?: TooltipProp,
): ModuleInfoDrawerProps | null => {
  if (!toolTipProps?.toolTipDescription) {
    return null;
  }

  const fallbackTitle = toolTipProps.toolTipTitle?.trim()
    ? `About ${title}`
    : `About ${title}`;

  return {
    title: fallbackTitle,
    markdownContent: `## Overview\n\n${toolTipProps.toolTipDescription.trim()}`,
    ariaLabel: fallbackTitle,
    iconButtonId: `${toolTipProps.clickId}_drawer_info`,
  };
};

const buildGenericDrawerProps = (title: string): ModuleInfoDrawerProps => ({
  title: `About ${title}`,
  markdownContent: `## What this page is\n\nThis page is part of the Hana dashboard for the current organization.\n\n## How to use it\n\nUse this page for the workflow shown here, then move into the related records, forms, or actions on the page for the exact operation you need.\n\n## Why this help exists\n\nThis side guide is shown so the page never falls back to an empty help experience.`,
  ariaLabel: `About ${title}`,
});

export const ModuleHeader = ({
  title,
  visualStyle = 'legacy',
  ModuleHeaderIcon,
  onModuleHeaderIconClick,
  toolTipProps,
  infoDrawerProps,
  rightContent,
}: ModuleHeaderProps) => {
  const registryDrawerDefinition = resolveModuleHeaderInfoByTitle(title);
  const registryDrawerProps = registryDrawerDefinition
    ? {
        ...registryDrawerDefinition,
        ariaLabel: registryDrawerDefinition.title,
      }
    : null;
  const tooltipFallbackDrawerProps = buildFallbackDrawerPropsFromTooltip(
    title,
    toolTipProps,
  );
  const baseDrawerProps =
    infoDrawerProps ??
    registryDrawerProps ??
    tooltipFallbackDrawerProps ??
    buildGenericDrawerProps(title);
  const shouldRenderTooltip = Boolean(toolTipProps && !baseDrawerProps);
  const resolvedDrawerProps = baseDrawerProps
    ? {
        ...baseDrawerProps,
        markdownContent: appendQuickLinksToDrawer(
          baseDrawerProps.markdownContent,
          toolTipProps,
        ),
      }
    : null;

  return (
    <Box sx={{ marginBottom: '8px' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {ModuleHeaderIcon && (
            <IconButton
              aria-label={`${title} icon`}
              sx={{ padding: '0 8px 0 0' }}
              onClick={onModuleHeaderIconClick}
              disabled={!onModuleHeaderIconClick}
            >
              {ModuleHeaderIcon}
            </IconButton>
          )}
          <Typography
            variant="h4"
            component="h3"
            fontSize={visualStyle === 'revamp' ? '20px' : '24px'}
            fontWeight={visualStyle === 'revamp' ? 700 : 'bold'}
            sx={(theme) => ({
              fontFamily:
                visualStyle === 'revamp'
                  ? 'Montserrat, Roboto, Arial, sans-serif'
                  : undefined,
              lineHeight: visualStyle === 'revamp' ? 1.3 : undefined,
              color:
                visualStyle === 'revamp'
                  ? theme.palette.text.primary
                  : undefined,
            })}
          >
            {visualStyle === 'revamp' ? title : title.toUpperCase()}
          </Typography>

          {shouldRenderTooltip && toolTipProps && (
            <CustomTooltip
              style={{
                backgroundColor: variableStyle.colorWhite,
                color: variableStyle.colorBlack,
                maxWidth: 240,
                marginRight: '20px',
              }}
              {...toolTipProps}
            />
          )}
          {resolvedDrawerProps && (
            <InfoDrawerTrigger {...resolvedDrawerProps} />
          )}
        </Box>
        {rightContent && <Box>{rightContent}</Box>}
      </Box>
      <CustomDivider orientation="horizontal" sx={{ marginTop: '8px' }} />
    </Box>
  );
};
